import { useQuery } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { Activity, Clock, Zap } from "lucide-react";

interface UsageRecord {
  id: number;
  taskType: string;
  taskName: string;
  creditsConsumed: number;
  executedAt: string;
  metadata?: any;
  llmModel?: string;
}

export function UsageHistoryTable() {
  const { data: usageHistory, isLoading } = useQuery<UsageRecord[]>({
    queryKey: ["/api/user/usage-history"],
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!usageHistory || usageHistory.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Activity className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Usage History</h3>
          <p className="text-muted-foreground text-center">
            Start creating stories to see your AI model usage and credit consumption here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const getTaskTypeColor = (taskType: string) => {
    switch (taskType) {
      case "blueprint_generation": return "bg-blue-100 text-blue-800";
      case "chapter_generation": return "bg-green-100 text-green-800";
      case "editing": return "bg-purple-100 text-purple-800";
      case "analysis": return "bg-orange-100 text-orange-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatTaskType = (taskType: string) => {
    return taskType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Task</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Credits</TableHead>
            <TableHead>Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {usageHistory.map((record) => (
            <TableRow key={record.id}>
              <TableCell>
                <div className="space-y-1">
                  <div className="font-medium">{record.taskName}</div>
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${getTaskTypeColor(record.taskType)}`}
                  >
                    {formatTaskType(record.taskType)}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <div className="font-mono text-sm">
                  {record.llmModel || "system"}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <span className="font-medium">{record.creditsConsumed}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <Clock className="h-4 w-4" />
                  {formatDistanceToNow(new Date(record.executedAt), { addSuffix: true })}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {usageHistory.length >= 50 && (
        <div className="text-center text-sm text-muted-foreground">
          Showing last 50 activities
        </div>
      )}
    </div>
  );
}